<template>
  <div class="flex gap-1 items-center">
    <WatchlistIcon />

    <NButton
      v-gid="{
        module: 'order',
        screen: 'home',
        section: 'symbolDetailSearch',
        component: 'buyButton',
      }"
      size="mini"
      variant="fill"
      @click.stop="openOrderDialog(ORDER_TYPES.buy)"
    >
      <NText variant="overline" weight="medium">
        {{ t('common.buy') }}
      </NText>
    </NButton>

    <NButton
      v-gid="{
        module: 'order',
        screen: 'home',
        section: 'symbolDetailSearch',
        component: 'sellButton',
      }"
      size="mini"
      color="red"
      variant="fill"
      :disabled="offersIsin.includes(activeIsin)"
      @click.stop="openOrderDialog(ORDER_TYPES.sell)"
    >
      <NText variant="overline" weight="medium">
        {{ t('common.sell') }}
      </NText>
    </NButton>

    <NPopper hover offset-y="8">
      <NegarMarketDepthIcon
        v-gid="{
          module: 'symbol',
          screen: 'home',
          section: 'symbolDetailSearchMarketDepth',
          component: isShowMarketDepth
            ? 'closeMarketDepthButton'
            : 'openMarketDepthButton',
        }"
        size="20"
        class="cursor-pointer"
        :class="{
          'text-neutral-primary-disable !cursor-default': marketDepthDisabled,
        }"
        @click.stop="emit('handleMarketDepth')"
      />
      <template #content>
        <div class="bg-neutral-quaternary px-2 py-1 rounded-md text-right">
          <NText
            variant="caption2"
            weight="medium"
            class="text-neutral-quaternary whitespace-pre"
          >
            {{
              marketDepthDisabled
                ? t('symbol.disableMarketDepth')
                : t('symbol.marketDepth')
            }}
          </NText>
        </div>
      </template>
    </NPopper>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { NegarMarketDepthIcon } from '@negar/icons';
import { useLiveInitialPublicOffers, IPO_INITIAL_STATES } from '@core/ipo';
import { ORDER_TYPES } from '@core/order';

import { WatchlistIcon } from '@/modules/watchlist';
import { useOrderDialogs } from '@/modules/order';
import { useActiveSymbolIsin, useLiveSymbolQuery } from '@/modules/symbol';

type SymbolActionsEmits = {
  (e: 'handleMarketDepth'): void;
};

type SymbolActionsProps = {
  isShowMarketDepth?: boolean;
  marketDepthDisabled?: boolean;
};

const emit = defineEmits<SymbolActionsEmits>();

defineProps<SymbolActionsProps>();

const { t } = useI18n();

const { offersIsin } = useLiveInitialPublicOffers();

const { openNewOrderFormDialog } = useOrderDialogs();

const { activeIsin } = useActiveSymbolIsin();
const { symbol } = useLiveSymbolQuery(activeIsin);

const openOrderDialog = (type: 'buy' | 'sell') => {
  openNewOrderFormDialog({
    type,
    symbol: {
      isin: symbol.value.isin as string,
      instrumentName: symbol.value.instrumentName as string,
      instrumentTypeCode: symbol.value.instrumentTypeCode as string,
    },
  });
};
</script>
